import { ImageIcon, type LucideIcon } from "lucide-react"
import { cn } from "@workspace/ui/lib/utils"

type ImageSlotProps = {
  ratio?: string
  tone?: "neutral" | "brand" | "dark"
  caption?: string
  src?: string
  alt?: string
  /** Optional icon shown instead of the generic image placeholder. */
  icon?: LucideIcon
  className?: string
}

const TONE: Record<NonNullable<ImageSlotProps["tone"]>, string> = {
  neutral:
    "bg-[linear-gradient(135deg,oklch(0.93_0.03_253)_0%,oklch(0.88_0.06_263)_60%,oklch(0.84_0.1_268)_100%)]",
  brand:
    "bg-[linear-gradient(135deg,oklch(0.88_0.08_265)_0%,oklch(0.65_0.22_268)_70%,oklch(0.45_0.26_268)_100%)] text-white",
  dark:
    "bg-[linear-gradient(135deg,oklch(0.24_0.04_263)_0%,oklch(0.18_0.03_263)_60%,oklch(0.12_0.02_263)_100%)] text-white/80",
}

export function ImageSlot({
  ratio = "4 / 3",
  tone = "neutral",
  caption,
  src,
  alt,
  icon: Icon,
  className,
}: ImageSlotProps) {
  return (
    <div
      className={cn(
        "relative w-full overflow-hidden border border-border",
        !src && TONE[tone],
        className,
      )}
      style={{ aspectRatio: ratio }}
      role={src ? undefined : "img"}
      aria-label={src ? undefined : (alt ?? caption ?? "Image placeholder")}
    >
      {src ? (
        <img
          src={src}
          alt={alt ?? ""}
          loading="lazy"
          className="h-full w-full object-cover"
        />
      ) : (
        <>
          {/* Diagonal rules as decorative texture */}
          <svg
            aria-hidden
            className="absolute inset-0 h-full w-full opacity-[0.18]"
            preserveAspectRatio="none"
          >
            <defs>
              <pattern
                id="diag"
                width="14"
                height="14"
                patternUnits="userSpaceOnUse"
                patternTransform="rotate(-35)"
              >
                <line
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="14"
                  stroke="currentColor"
                  strokeWidth="1"
                />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#diag)" />
          </svg>

          {/* Centered hint */}
          <div className="absolute inset-0 grid place-items-center">
            <div className="flex flex-col items-center gap-3 opacity-80">
              {Icon ? (
                <Icon className="size-12" strokeWidth={1.3} />
              ) : (
                <ImageIcon className="size-8" strokeWidth={1.2} />
              )}
              <span className="font-mono text-[10px] uppercase tracking-[0.28em]">
                {caption ?? "Image"}
              </span>
            </div>
          </div>

          {/* Corner index */}
          <span className="eyebrow-sm absolute left-3 top-3 opacity-60">
            ○
          </span>
          <span className="eyebrow-sm absolute right-3 bottom-3 opacity-60">
            4×3
          </span>
        </>
      )}
    </div>
  )
}
